
import { useState, useRef, useCallback } from "react";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700;900&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Roboto', sans-serif; }

  .emi-page {
    background: #efefef;
    min-height: 70vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 20px;
  }

  .emi-container {
    width: 100%;
    max-width: 1260px;
  }

  .emi-heading {
    text-align: center;
    font-size: 2rem;
    font-weight: 700;
    color: #CF2230;
    margin-bottom: 32px;
    letter-spacing: 0;
  }

  /* Card */
  .emi-card {
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 3px;
    display: flex;
    overflow: hidden;
    box-shadow: 0 2px 12px rgba(0,0,0,0.07);
  }

  /* Red sidebar */
  .emi-red-bar {
    background: #CF2230;
    width: 76px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* Main content */
  .emi-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  /* Top: sliders */
  .emi-top {
    padding: 26px 30px 22px 30px;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 22px;
    border-bottom: 1px solid #e8e8e8;
  }

  /* Bottom: details */
  .emi-bottom {
    padding: 20px 30px 26px 30px;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 22px;
  }

  /* Right: car price */
  .emi-price-col {
    width: 185px;
    flex-shrink: 0;
    border-left: 1px solid #e8e8e8;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
  }

  .car-price-label {
    font-size: 0.7rem;
    font-weight: 700;
    color: #1a1a1a;
    letter-spacing: 1px;
    text-transform: uppercase;
    margin-bottom: 10px;
  }

  .car-price-value {
    font-size: 1.75rem;
    font-weight: 900;
    color: #111;
    letter-spacing: -0.5px;
    white-space: nowrap;
  }

  /* Slider group */
  .sg-label {
    display: block;
    font-size: 0.67rem;
    font-weight: 500;
    color: #999;
    letter-spacing: 1px;
    text-transform: uppercase;
    margin-bottom: 5px;
  }

  .sg-value {
    display: block;
    font-size: 1.15rem;
    font-weight: 700;
    color: #111;
    margin-bottom: 14px;
  }

  .custom-slider {
    position: relative;
    height: 20px;
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
    margin-bottom: 6px;
  }

  .custom-slider-track {
    position: absolute;
    left: 0;
    right: 0;
    height: 3px;
    border-radius: 2px;
    background: #d0d0d0;
    overflow: visible;
  }

  .custom-slider-fill {
    height: 100%;
    background: #CF2230;
    border-radius: 2px;
    position: relative;
  }

  .custom-slider-thumb {
    position: absolute;
    right: -9px;
    top: 50%;
    transform: translateY(-50%);
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #CF2230;
    box-shadow: 0 1px 6px rgba(207,34,48,0.45);
    transition: transform 0.1s ease, box-shadow 0.1s ease;
    cursor: grab;
  }

  .custom-slider-thumb:active,
  .custom-slider-thumb.dragging {
    cursor: grabbing;
    transform: translateY(-50%) scale(1.18);
    box-shadow: 0 2px 10px rgba(207,34,48,0.55);
  }

  .sg-hints {
    display: flex;
    justify-content: space-between;
    font-size: 0.66rem;
    color: #bbb;
  }

  /* Detail group */
  .dg-label {
    display: block;
    font-size: 0.67rem;
    font-weight: 500;
    color: #999;
    letter-spacing: 1px;
    text-transform: uppercase;
    margin-bottom: 8px;
  }

  .dg-value {
    font-size: 1.5rem;
    font-weight: 900;
    color: #111;
    letter-spacing: -0.5px;
  }

  /* ── Tablet (≤ 900px) ── */
  @media (max-width: 900px) {
    .emi-red-bar {
      width: 52px;
    }

    .emi-red-bar svg {
      width: 32px;
      height: 32px;
    }

    .emi-top,
    .emi-bottom {
      padding: 20px 20px 18px 20px;
      gap: 16px;
    }

    .emi-price-col {
      width: 145px;
      padding: 16px 12px;
    }

    .car-price-value {
      font-size: 1.35rem;
    }

    .dg-value {
      font-size: 1.2rem;
    }
  }

  /* ── Small tablet / large mobile (≤ 700px) ── */
  @media (max-width: 700px) {
    .emi-heading {
      font-size: 1.5rem;
      margin-bottom: 20px;
    }

    .emi-page {
      padding: 32px 14px;
    }

    /* Stack card vertically: hide red sidebar, show top bar */
    .emi-card {
      flex-direction: column;
    }

    .emi-red-bar {
      width: 100%;
      height: 56px;
      flex-direction: row;
      gap: 12px;
    }

    .emi-red-bar::after {
      content: "EMI Calculator";
      color: #fff;
      font-family: 'Roboto', sans-serif;
      font-size: 1rem;
      font-weight: 700;
      letter-spacing: 1px;
      text-transform: uppercase;
    }

    /* Car price goes to top of main content as a banner row */
    .emi-price-col {
      order: -1;
      width: 100%;
      border-left: none;
      border-bottom: 1px solid #e8e8e8;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      padding: 14px 20px;
    }

    .car-price-label {
      margin-bottom: 0;
      font-size: 0.65rem;
    }

    .car-price-value {
      font-size: 1.4rem;
    }

    /* Sliders: 1 column */
    .emi-top {
      grid-template-columns: 1fr;
      padding: 18px 16px 14px 16px;
      gap: 20px;
    }

    /* Details: 3 columns still fit */
    .emi-bottom {
      grid-template-columns: 1fr 1fr 1fr;
      padding: 14px 16px 20px 16px;
      gap: 10px;
    }

    .dg-value {
      font-size: 1rem;
    }

    .dg-label {
      font-size: 0.6rem;
    }
  }

  /* ── Mobile (≤ 420px) ── */
  @media (max-width: 420px) {
    .emi-heading {
      font-size: 1.25rem;
    }

    .emi-bottom {
      grid-template-columns: 1fr;
      gap: 14px;
    }

    .dg-value {
      font-size: 1.3rem;
    }

    .car-price-value {
      font-size: 1.25rem;
    }
  }
`;

function formatINR(val) {
  return "₹" + Number(Math.round(val)).toLocaleString("en-IN");
}

function calcEMI(principal, annualRate, years) {
  if (!principal || !annualRate || !years) return 0;
  const r = annualRate / 12 / 100;
  const n = years * 12;
  return Math.round((principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1));
}

function snap(value, step) {
  return Math.round(value / step) * step;
}

function CustomSlider({ value, min, max, step, onChange }) {
  const trackRef = useRef(null);
  const dragging = useRef(false);
  const [isDragging, setIsDragging] = useState(false);

  const pct = ((value - min) / (max - min)) * 100;

  const getValueFromEvent = useCallback((clientX) => {
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const raw = min + ratio * (max - min);
    return Math.min(max, Math.max(min, snap(raw, step)));
  }, [min, max, step]);

  const onMouseDown = useCallback((e) => {
    e.preventDefault();
    dragging.current = true;
    setIsDragging(true);
    onChange(getValueFromEvent(e.clientX));

    const onMove = (me) => { if (dragging.current) onChange(getValueFromEvent(me.clientX)); };
    const onUp = () => {
      dragging.current = false;
      setIsDragging(false);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [getValueFromEvent, onChange]);

  const onTouchStart = useCallback((e) => {
    e.preventDefault();
    dragging.current = true;
    setIsDragging(true);
    onChange(getValueFromEvent(e.touches[0].clientX));

    const onMove = (te) => { if (dragging.current) onChange(getValueFromEvent(te.touches[0].clientX)); };
    const onEnd = () => {
      dragging.current = false;
      setIsDragging(false);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
    };
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onEnd);
  }, [getValueFromEvent, onChange]);

  return (
    <div className="custom-slider" ref={trackRef} onMouseDown={onMouseDown} onTouchStart={onTouchStart}>
      <div className="custom-slider-track">
        <div className="custom-slider-fill" style={{ width: `${pct}%` }}>
          <div className={`custom-slider-thumb${isDragging ? " dragging" : ""}`} />
        </div>
      </div>
    </div>
  );
}

function SliderGroup({ label, displayValue, value, min, max, step, onChange, hintFrom, hintTo }) {
  return (
    <div>
      <span className="sg-label">{label}</span>
      <span className="sg-value">{displayValue}</span>
      <CustomSlider value={value} min={min} max={max} step={step} onChange={onChange} />
      <div className="sg-hints">
        <span>{hintFrom}</span>
        <span>{hintTo}</span>
      </div>
    </div>
  );
}

const CalcIcon = () => (
  <svg width="44" height="44" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="6" y="4" width="36" height="40" rx="4" stroke="white" strokeWidth="2.5" fill="none"/>
    <rect x="12" y="13" width="6" height="6" rx="1" fill="white"/>
    <rect x="21" y="13" width="6" height="6" rx="1" fill="white"/>
    <rect x="30" y="13" width="6" height="6" rx="1" fill="white"/>
    <rect x="12" y="22" width="6" height="6" rx="1" fill="white"/>
    <rect x="21" y="22" width="6" height="6" rx="1" fill="white"/>
    <rect x="30" y="22" width="6" height="6" rx="1" fill="white"/>
    <rect x="12" y="31" width="6" height="6" rx="1" fill="white"/>
    <rect x="21" y="31" width="6" height="6" rx="1" fill="white"/>
    <rect x="30" y="31" width="6" height="6" rx="1" fill="white"/>
  </svg>
);

export default function EMICalculator() {
  const [loanAmount, setLoanAmount] = useState(10000);
  const [interestRate, setInterestRate] = useState(10);
  const [tenure, setTenure] = useState(4);

  const emi = calcEMI(loanAmount, interestRate, tenure);

  return (
    <>
      <style>{styles}</style>
      <div className="emi-page">
        <div className="emi-container">
          <h2 className="emi-heading">EMI Calculator</h2>

          <div className="emi-card">
            {/* Red sidebar */}
            <div className="emi-red-bar"><CalcIcon /></div>

            {/* Main */}
            <div className="emi-main">
              {/* Sliders */}
              <div className="emi-top">
                <SliderGroup
                  label="LOAN AMOUNT (₹)" displayValue={formatINR(loanAmount)}
                  value={loanAmount} min={10000} max={999000} step={1000}
                  onChange={setLoanAmount} hintFrom="from ₹10K" hintTo="to ₹9.99L"
                />
                <SliderGroup
                  label="INTEREST RATE (%) P.A" displayValue={`${interestRate}%`}
                  value={interestRate} min={7} max={15} step={0.5}
                  onChange={setInterestRate} hintFrom="from 7%" hintTo="to 15%"
                />
                <SliderGroup
                  label="TENURE (YEARS)" displayValue={`${tenure} Years`}
                  value={tenure} min={1} max={8} step={1}
                  onChange={setTenure} hintFrom="from 1 year" hintTo="to 8 years"
                />
              </div>

              {/* Details */}
              <div className="emi-bottom">
                <div>
                  <span className="dg-label">LOAN AMOUNT</span>
                  <div className="dg-value">{formatINR(loanAmount)}</div>
                </div>
                <div>
                  <span className="dg-label">DOWN PAYMENT</span>
                  <div className="dg-value">₹50,000</div>
                </div>
                <div>
                  <span className="dg-label">EMI AMOUNT</span>
                  <div className="dg-value">{formatINR(emi)}</div>
                </div>
              </div>
            </div>

            {/* Car price */}
            <div className="emi-price-col">
              <div className="car-price-label">CAR PRICE</div>
              <div className="car-price-value">₹9,99,800*</div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}